import Dexie from 'dexie'
import { DataCacheBase } from './data-cache-base'

interface DbIndex<K> {
  key: K
  seq: number
}

interface DbItem<K, T> {
  key: K
  data: T
}

//--------------------------------------------------------------------------
// CacheDB
//--------------------------------------------------------------------------
class CacheDB<K, T> extends Dexie {
  items!: Dexie.Table<DbItem<K, T>, K>
  index!: Dexie.Table<DbIndex<K>, K>

  constructor (dbName: string) {
    super(dbName)
    this.version(2).stores({
      items: 'key',
      index: 'key',
    })
  }
}

//--------------------------------------------------------------------------
// DataDbIndexedDB
//--------------------------------------------------------------------------
export class DataCacheIndexedDB<K extends string | number, T> extends DataCacheBase<K, T> {
  db!: CacheDB<K, T>

  constructor (dbName: string, maxCount: number, maxCacheCount: number, bgPoll = 3000) {
    super(dbName, maxCount, maxCacheCount, bgPoll)
    this.db = new CacheDB<K, T>(dbName)
  }

  async _initialize () {
    const index = await this.db.index.toArray()
    return index.sort((a, b) => a.seq - b.seq).map((ind) => ({ key: ind.key, seq: ind.seq }))
  }

  protected async _reset (): Promise<void> {
    await this.db.items.clear()
    await this.db.index.clear()
  }

  protected async _get (key: K): Promise<T | null> {
    const item = await this.db.items.get({ key })
    if (item) {
      return item.data
    }
    return null
  }

  protected async _set (key: K, seq: number, data: T): Promise<void> {
    await this.db.index.put({ key, seq })
    await this.db.items.put({ key, data })
  }

  protected async _touch (key: K, seq: number): Promise<void> {
    await this.db.index.put({ key, seq })
  }

  protected async _delete (key: K): Promise<void> {
    await this.db.items.delete(key)
    await this.db.index.delete(key)
  }

  static async clearDB<K, T> (dbName: string) {
    const db = new CacheDB<K, T>(dbName)
    await db.items.clear()
    await db.index.clear()
    db.close()
  }
}
